import type {CopilotAssignmentRepository} from '@github-ui/agent-sessions/components/copilot-assignment/copilot-assignment-types'
import {DEFAULT_MAXIMUM_ASSIGNEES} from '@github-ui/item-picker-assignee'
import {createContext, useCallback, useContext, useMemo, useState} from 'react'

import {MemexColumnDataType} from '../../api/columns/contracts/memex-column'
import type {UpdateColumnValueAction} from '../../api/columns/contracts/storage'
import type {IAssignee} from '../../api/common-contracts'
import {ItemType} from '../../api/memex-items/item-type'
import type {FieldGrouping} from '../../features/grouping/types'
import {isCopilot} from '../../helpers/assignee-helper'
import {getInitialState} from '../../helpers/initial-state'
import type {LoggedInUser} from '../../helpers/json-island'
import type {MemexItemModel} from '../../models/memex-item-model'

type CopilotAssignmentModalState = {
  isOpen: boolean
  repository: CopilotAssignmentRepository | null
  issueNumbers: Array<number>
  onSuccess?: () => Promise<void>
}

export type CopilotModalForIssuesProps = (
  items: Array<MemexItemModel>,
  onSuccess: () => Promise<void>,
  repositoryFilter?: (item: MemexItemModel) => boolean,
) => boolean

type CopilotAssignmentModalContextValue = {
  modalState: CopilotAssignmentModalState
  openModal: (
    repository: CopilotAssignmentRepository,
    issueNumbers: Array<number>,
    onSuccess?: () => Promise<void>,
  ) => void
  closeModal: () => void
  handleAssignmentComplete: () => Promise<void>
  tryOpenCopilotModal: (
    items: Array<MemexItemModel>,
    sourceObject: FieldGrouping,
    onSuccess: () => Promise<void>,
  ) => boolean
  openCopilotModalForIssues: CopilotModalForIssuesProps
}

const CopilotAssignmentModalContext = createContext<CopilotAssignmentModalContextValue | null>(null)

export const CopilotAssignmentModalProvider: React.FC<{children: React.ReactNode}> = ({children}) => {
  const [modalState, setModalState] = useState<CopilotAssignmentModalState>({
    isOpen: false,
    repository: null,
    issueNumbers: [],
  })

  const openModal = useCallback(
    (repository: CopilotAssignmentRepository, issueNumbers: Array<number>, onSuccess?: () => Promise<void>) => {
      setModalState({
        isOpen: true,
        repository,
        issueNumbers,
        onSuccess,
      })
    },
    [],
  )

  const closeModal = useCallback(() => {
    setModalState({
      isOpen: false,
      repository: null,
      issueNumbers: [],
    })
  }, [])

  const handleAssignmentComplete = useCallback(async () => {
    await modalState.onSuccess?.()
    closeModal()
  }, [closeModal, modalState])

  const openCopilotModalForIssues = useCallback(
    (
      items: Array<MemexItemModel>,
      onSuccess: () => Promise<void>,
      repositoryFilter?: (item: MemexItemModel) => boolean,
    ): boolean => {
      // Filter for issues only to get repository info and issue numbers
      const issueItems = items.filter(item => {
        const nameWithOwner = item.getNameWithOwner()
        const issueNumber = item.getItemNumber()
        const isValidIssue =
          item.contentType === ItemType.Issue && nameWithOwner && nameWithOwner.length === 2 && issueNumber > 0

        // Apply additional repository filter if provided
        return isValidIssue && (repositoryFilter ? repositoryFilter(item) : true)
      })

      if (issueItems.length === 0) {
        return false
      }

      // Use the first qualified issue for repository information
      const firstIssue = issueItems[0]
      if (!firstIssue) return false

      const nameWithOwner = firstIssue.getNameWithOwner()
      const [owner, repo] = nameWithOwner as [string, string]

      const repository: CopilotAssignmentRepository = {
        name: repo,
        owner: {
          login: owner,
        },
      }

      openModal(
        repository,
        issueItems.map(item => item.getItemNumber()),
        onSuccess,
      )
      return true
    },
    [openModal],
  )

  const tryOpenCopilotModal = useCallback(
    (items: Array<MemexItemModel>, sourceObject: FieldGrouping, onSuccess: () => Promise<void>): boolean => {
      // Check if we should open the Copilot assignment modal for any of the selected items
      if (!shouldOpenCopilotModal(items, sourceObject)) {
        return false
      }

      // Open the modal
      return openCopilotModalForIssues(items, onSuccess)
    },
    [openCopilotModalForIssues],
  )

  const contextValue: CopilotAssignmentModalContextValue = useMemo(
    () => ({
      handleAssignmentComplete,
      modalState,
      openModal,
      closeModal,
      tryOpenCopilotModal,
      openCopilotModalForIssues,
    }),
    [handleAssignmentComplete, modalState, openModal, closeModal, tryOpenCopilotModal, openCopilotModalForIssues],
  )

  return (
    <CopilotAssignmentModalContext.Provider value={contextValue}>{children}</CopilotAssignmentModalContext.Provider>
  )
}

export function useCopilotAssignmentModal(): CopilotAssignmentModalContextValue {
  const context = useContext(CopilotAssignmentModalContext)

  if (!context) {
    throw new Error('Must use `useCopilotAssignmentModal` in child of `<CopilotAssignmentModalProvider>`')
  }

  return context
}

export function addActorToAssignees(assignees: Array<IAssignee>, actor: LoggedInUser | undefined): Array<IAssignee> {
  if (!actor) {
    return assignees
  }

  if (assignees.some(assignee => assignee.id === actor.id)) {
    return assignees
  }

  if (assignees.length >= DEFAULT_MAXIMUM_ASSIGNEES) {
    return assignees
  }

  return [...assignees, actor]
}

export function getCopilotAssigneeAction({
  targetAssignees,
  existingAssignees,
  keepExistingAssignees,
}: {
  targetAssignees: Array<IAssignee>
  existingAssignees: Array<IAssignee>
  keepExistingAssignees: boolean
}): UpdateColumnValueAction {
  const {loggedInUser} = getInitialState()

  let baseAssignees: Array<IAssignee>
  if (keepExistingAssignees) {
    const newAssignees = targetAssignees.filter(
      target => !existingAssignees.some(existing => existing.id === target.id),
    )
    baseAssignees = [...existingAssignees, ...newAssignees]
  } else {
    baseAssignees = [...targetAssignees]
  }

  const finalAssignees = addActorToAssignees(baseAssignees, loggedInUser)

  return {
    dataType: MemexColumnDataType.Assignees,
    value: finalAssignees,
  }
}

/**
 * Determines if the Copilot assignment modal should be opened for the given items
 */
export function shouldOpenCopilotModal(
  itemsToMove: Array<MemexItemModel>,
  sourceObject: FieldGrouping | UpdateColumnValueAction,
): boolean {
  // Check if the source object has a Copilot assignee
  const isCopilotAssignment =
    sourceObject.dataType === MemexColumnDataType.Assignees &&
    Array.isArray(sourceObject.value) &&
    sourceObject.value.some(item => isCopilot(item.login))

  if (!isCopilotAssignment) {
    return false
  }

  const issues = itemsToMove.filter(item => item.contentType === ItemType.Issue)

  if (issues.length === 0) {
    return false
  }

  // Check if Copilot is already assigned to all issues
  const allIssuesHaveCopilot = issues.every(issue => {
    const assignees = issue.columns.Assignees ?? []
    return assignees.some(assignee => isCopilot(assignee.login))
  })

  // Only show modal if at least one issue doesn't have Copilot assigned
  return !allIssuesHaveCopilot
}

try{ CopilotAssignmentModalContext.displayName ||= 'CopilotAssignmentModalContext' } catch {}
try{ CopilotAssignmentModalProvider.displayName ||= 'CopilotAssignmentModalProvider' } catch {}